import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { Zap, FileText, TrendingUp, ScanText, Radio, ShieldCheck, CheckCircle2, ArrowRight, Trophy } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1741699427706-7bfb38c716d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const RANKER = "https://images.unsplash.com/photo-1690223878423-14cf4867638f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const features = [
  { icon: FileText, title: "Real CBT Mock Tests", desc: "NTA/TCS-iON style exam interface with live palette, timer & instant results." },
  { icon: TrendingUp, title: "Deep Analytics", desc: "Subject, chapter & topic-wise accuracy, percentile, rank and progress trends." },
  { icon: ScanText, title: "AI OCR Import", desc: "Teachers upload papers; AI structures questions & diagrams for review." },
  { icon: Radio, title: "Live Classes", desc: "Scheduled live sessions with recordings, reminders and attendance." },
  { icon: Trophy, title: "Leaderboards", desc: "Compete on every test with deterministic, fair ranking." },
  { icon: ShieldCheck, title: "Verified Content", desc: "Every question passes teacher review & admin approval before going live." },
];

export default function Landing() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  useEffect(() => { get("/taxonomy?level=category").then(setCats).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="h-16 flex items-center px-5 sm:px-10 border-b border-slate-100 sticky top-0 glass z-30">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl rt-gradient flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
          <span className="font-display font-extrabold text-xl text-slate-900">RankTest</span>
        </div>
        <nav className="ml-auto flex items-center gap-3">
          <button onClick={() => nav("/auth")} data-testid="landing-login-btn" className="text-sm font-semibold text-slate-700 px-4 py-2 hover:text-indigo-800">Log in</button>
          <button onClick={() => nav("/auth?mode=register")} data-testid="landing-signup-btn" className="text-sm font-semibold text-white bg-indigo-800 px-5 py-2.5 rounded-full hover:bg-indigo-900 transition">Start Free</button>
        </nav>
      </header>

      <section className="rt-gradient text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fadeUp">
            <span className="text-xs uppercase font-bold tracking-widest text-amber-300">India's Exam Prep, Reimagined</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mt-3">
              Crack SSC, Banking, Railways & UPSC with <span className="text-amber-400">RankTest</span>
            </h1>
            <p className="text-base sm:text-lg text-indigo-100 mt-5 max-w-lg">
              50,000+ mock tests, live classes, AI-powered practice and analytics that pinpoint exactly where to improve.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => nav("/auth?mode=register")} data-testid="hero-get-started-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-7 py-3.5 rounded-full flex items-center gap-2 transition">
                Get Started Free <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => nav("/auth")} data-testid="hero-explore-btn" className="bg-white/10 border border-white/30 backdrop-blur px-7 py-3.5 rounded-full font-semibold hover:bg-white/20 transition">Explore Demo</button>
            </div>
            <div className="flex items-center gap-6 mt-9 text-sm text-indigo-100">
              <div><span className="font-display font-extrabold text-2xl text-white">2.4M+</span><br />Aspirants</div>
              <div><span className="font-display font-extrabold text-2xl text-white">50K+</span><br />Mock Tests</div>
              <div><span className="font-display font-extrabold text-2xl text-white">4.8★</span><br />Rated</div>
            </div>
          </div>
          <div className="relative animate-fadeUp">
            <img src={HERO} alt="Aspirant studying" className="rounded-2xl shadow-2xl w-full object-cover h-[420px]" />
            <div className="absolute -bottom-5 -left-5 bg-white text-slate-900 rounded-xl shadow-xl p-4 hidden sm:block">
              <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /><span className="font-bold">AIR 1 — Priya S.</span></div>
              <p className="text-xs text-slate-500 mt-1">"RankTest mocks felt exactly like the real exam."</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-10 py-14">
        <p className="text-xs uppercase font-bold tracking-widest text-indigo-700">Choose your exam</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2">Prepare for every major exam</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
          {cats.map((c) => (
            <button key={c.id} onClick={() => nav("/auth")} data-testid={`cat-${c.id}`} className={`text-left rounded-xl border p-5 card-hover ${c.color || "bg-slate-50 border-slate-200"}`}>
              <p className="font-display font-bold text-lg">{c.name}</p>
              <p className="text-sm opacity-80 mt-1">{c.badge}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Everything you need to rank higher</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-6 card-hover">
                <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center"><f.icon className="h-5 w-5" /></div>
                <h3 className="font-display font-semibold text-lg mt-4">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <img src={RANKER} alt="Toppers celebrating" className="rounded-2xl shadow-xl h-80 w-full object-cover" />
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Join thousands of selected candidates</h2>
          <ul className="mt-6 space-y-3">
            {["Adaptive practice tuned to your weak areas", "Detailed solutions with explanations & diagrams", "Live doubt solving with expert faculty", "Multi-language support (English & Hindi)"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5" /> {t}</li>
            ))}
          </ul>
          <button onClick={() => nav("/auth?mode=register")} className="mt-8 bg-indigo-800 text-white font-bold px-7 py-3.5 rounded-full hover:bg-indigo-900 transition" data-testid="cta-bottom-btn">Create your free account</button>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-500">
        © 2026 RankTest. Built for aspirants. Original platform — not affiliated with any other brand.
      </footer>
    </div>
  );
}
